import React from 'react';
import PropTypes from 'prop-types';
import { Carousel, CarouselItem } from '@americanexpress/dls-react';
import { IconChevronRight } from '@americanexpress/dls-icons';
import {
  useLoungeAirportIata,
  useLoungeAirportLinkUrl,
  useLoungeTerminal,
  useTranslatedAirportLounges,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { cn, formatText } from '@/helpers/utils';
import { useRouteParams } from '@/context/routeParamsContext';
import LFLink from './LFLink';
import RouteReplaceWithQueryParams from './RouteReplaceWithQueryParams';
import SearchLoungeResult from './SearchLoungeResult';
import Loading from './Loading';
import styles from './LoungeOtherLoungesInTerminal.module.scss';

const LoungeOtherLoungesInTerminal = ({ className }) => {
  const { loungeId, product } = useRouteParams();
  const terminal = useLoungeTerminal(loungeId);
  const link = useLoungeAirportLinkUrl(loungeId);
  const iata = useLoungeAirportIata(loungeId);
  const { data, error, isLoading } = useTranslatedAirportLounges();
  const { lounges } = useCommonContent();
  const otherLoungesLink = lounges?.otherLounges?.link;

  const otherLounges = (data || []).filter((lounge) => {
    return (
      Boolean(lounge) &&
      lounge.location.terminal === terminal &&
      lounge.id !== loungeId
    );
  });

  if (error) {
    return <RouteReplaceWithQueryParams to={`/${product}/`} />;
  }

  if (isLoading) {
    return <Loading containerClassName="tw-m-12 tw-flex tw-justify-center" />;
  }

  return (
    <div
      className={cn(
        'pad-3-t col-xs-12 tw-mt-10 tw-w-full tw-border-t tw-border-gray-300',
        className
      )}
    >
      <h3 className={cn('margin-3-b tw-heading-md-regular tw-text-gray-900')}>
        {formatText(lounges?.otherLounges?.header, { terminal })}
      </h3>
      {otherLounges.length === 0 ? (
        <div className="margin-3-b">
          {formatText(lounges?.otherLounges?.label, { terminal })}
        </div>
      ) : (
        <Carousel
          className={cn(styles.carousel, 'tw-mb-4')}
          isSlideFullWidth={false}
        >
          {otherLounges.map((lounge) => (
            <CarouselItem id={lounge.id} key={lounge.id}>
              <SearchLoungeResult
                headingLevel={4}
                lounge={lounge}
                trackElement="view other lounges in terminal"
                key={lounge.id}
              />
            </CarouselItem>
          ))}
        </Carousel>
      )}
      {otherLoungesLink && (
        <LFLink
          to={link}
          className={cn('tw-font-medium tw-text-link hover_tw-underline')}
        >
          {formatText(otherLoungesLink, { iata })}
          <IconChevronRight
            size="xs"
            isFilled={true}
            className={cn(
              'tw-mb-px tw-ml-0dot5 tw-mr-1 tw-text-blue-bright-shade-600-s8'
            )}
          />
        </LFLink>
      )}
    </div>
  );
};

export default LoungeOtherLoungesInTerminal;

LoungeOtherLoungesInTerminal.propTypes = {
  className: PropTypes.string,
};
